import { useCallback, useEffect, useState } from 'react';
import { get, post } from '../utils/api';
import { getOrCreateDeviceId } from '../utils/deviceId';
import { StockCategoryFilter } from '../types/stockCategories';

interface FamiliarityStats {
  yesCount: number;
  noCount: number;
  total: number;
  userVote: boolean | null;
}

interface UseFamiliarityOptions {
  symbol: string | null;
  category?: StockCategoryFilter;
  gameDate?: string;
  enabled?: boolean;
  isAuthenticated?: boolean;
}

interface UseFamiliarityReturn {
  stats: FamiliarityStats;
  isLoading: boolean;
  isVoting: boolean;
  hasVoted: boolean;
  userVote: boolean | null;
  submitVote: (heardOf: boolean) => Promise<void>;
}

const EMPTY_STATS: FamiliarityStats = { yesCount: 0, noCount: 0, total: 0, userVote: null };

export const useFamiliarity = (options: UseFamiliarityOptions): UseFamiliarityReturn => {
  const { symbol, category, gameDate, enabled = true, isAuthenticated = true } = options;

  const [stats, setStats] = useState<FamiliarityStats>(EMPTY_STATS);
  const [isLoading, setIsLoading] = useState(false);
  const [isVoting, setIsVoting] = useState(false);

  // Reset when dependencies change
  useEffect(() => {
    setStats(EMPTY_STATS);
  }, [symbol, category, gameDate]);

  const fetchStats = useCallback(async () => {
    if (!enabled || !symbol || !category || !gameDate) return;

    setIsLoading(true);
    try {
      const params: Record<string, string> = { category, date: gameDate };
      if (!isAuthenticated) {
        params.deviceId = getOrCreateDeviceId();
      }

      const response = await get<FamiliarityStats>(
        `/api/familiarity/${encodeURIComponent(symbol)}`,
        { params }
      );

      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('[useFamiliarity] Failed to fetch stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, symbol, category, gameDate, isAuthenticated]);

  const submitVote = useCallback(async (heardOf: boolean) => {
    if (!symbol || !category || !gameDate) return;

    setIsVoting(true);

    // Optimistic update
    setStats((prev) => {
      const newYes = prev.yesCount + (heardOf ? 1 : 0);
      const newNo = prev.noCount + (heardOf ? 0 : 1);
      return {
        yesCount: newYes,
        noCount: newNo,
        total: newYes + newNo,
        userVote: heardOf,
      };
    });

    try {
      const body: Record<string, unknown> = {
        heardOf,
        category,
        date: gameDate,
      };

      if (!isAuthenticated) {
        body.deviceId = getOrCreateDeviceId();
      }

      const response = await post<FamiliarityStats>(
        `/api/familiarity/${encodeURIComponent(symbol)}/vote`,
        body
      );

      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('[useFamiliarity] Failed to submit vote:', error);
      // Revert optimistic update
      await fetchStats();
    } finally {
      setIsVoting(false);
    }
  }, [symbol, category, gameDate, isAuthenticated, fetchStats]);

  // Auto-fetch on mount / deps change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    isLoading,
    isVoting,
    hasVoted: stats.userVote !== null,
    userVote: stats.userVote,
    submitVote,
  };
};

export default useFamiliarity;
